import { css, Interpolation, Theme } from '@emotion/react'
import Popover, { PopoverOrigin } from '@mui/material/Popover'
import { memo, ReactNode, useCallback, useState } from 'react'
import ButtonInfo from '../Buttons/ButtonInfo'
import { getThemeSpacing } from '../styles/appTheme'
import APP_BREAKPOINTS, { maxWindowWidth } from '../styles/breakpoints'
import { PAPER_HEIGHTS } from '../theme'

const rootStyles = css({
    alignItems: 'center',
    display: 'flex',
    '& .MuiPopover-paper': {
        zIndex: PAPER_HEIGHTS.tooltip,
    },
})
const popoverStyles = css({
    fontSize: '0.8125rem',
    '& .MuiPaper-root': {
        padding: getThemeSpacing(1),
        alignItems: 'center',
        display: 'flex',
        maxWidth: 420,
        [maxWindowWidth(APP_BREAKPOINTS.sm)]: {
            maxWidth: 280,
        },
    },
})

export const defaultAnchorOrigin: PopoverOrigin = {
    vertical: 'bottom',
    horizontal: 'center',
} as const
export const defaultTransformOrigin: PopoverOrigin = {
    vertical: 'top',
    horizontal: 'center',
} as const

interface TooltipProps {
    /** must be a single memoized component */
    children: ReactNode
    styleOverrides?: Interpolation<Theme>
    iconStyleOverrides?: Interpolation<Theme>
    anchorOrigin?: PopoverOrigin
    transformOrigin?: PopoverOrigin
    fontSize?: 'small' | 'medium' | 'large' | 'inherit'
}

const Tooltip = (props: TooltipProps) => {
    const [anchorEl, setAnchorEl] = useState(null)

    const handleClick = useCallback((event) => {
        setAnchorEl(event.currentTarget)
    }, [])

    const handleClose = useCallback(() => {
        setAnchorEl(null)
    }, [])

    const open = Boolean(anchorEl)
    const id = open ? 'tooltip-popover' : undefined

    const {
        fontSize,
        children,
        styleOverrides,
        iconStyleOverrides,
        anchorOrigin = defaultAnchorOrigin,
        transformOrigin = defaultTransformOrigin,
    } = props

    return (
        <div css={[rootStyles, styleOverrides]}>
            <ButtonInfo
                fontSize={fontSize}
                onClick={handleClick}
                overrides={iconStyleOverrides}
            />
            <Popover
                id={id}
                style={{ zIndex: PAPER_HEIGHTS.tooltip }}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={anchorOrigin}
                transformOrigin={transformOrigin}
                css={popoverStyles}
            >
                {children}
            </Popover>
        </div>
    )
}

export default memo(Tooltip)
